/**
 * SuggestionsManager - Block analysis and suggestion handling
 *
 * Phase 5 Code Quality - Task 9.0
 *
 * Handles block analysis, suggestion caching, and badge management
 * for the smart suggestions feature.
 *
 * Usage:
 *   const manager = new SuggestionsManager({
 *     websiteId: 'uuid',
 *     pageId: 'uuid',
 *     csrfToken: 'token',
 *     onSuggestionsUpdated: (blockId, suggestions) => { ... },
 *     onBadgeClick: (blockId, suggestions) => { ... }
 *   })
 *
 *   await manager.analyzeBlock(blockId)
 *   await manager.analyzeAllBlocks()
 */

import { createLogger } from './logger.js'
import { getPageContext, getCsrfToken } from './config_manager.js'

const logger = createLogger('SuggestionsManager')

// Cache TTL in milliseconds
const DEFAULT_CACHE_TTL = 60000 // 1 minute

// Severity levels
export const Severity = {
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
}

// Severity icons (SVG)
export const SEVERITY_ICONS = {
  [Severity.ERROR]: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
  </svg>`,
  [Severity.WARNING]: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
  </svg>`,
  [Severity.INFO]: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
  </svg>`
}

export class SuggestionsManager {
  /**
   * @param {Object} options
   * @param {string} options.websiteId - Website UUID
   * @param {string} options.pageId - Page UUID
   * @param {string} options.csrfToken - CSRF token for API calls
   * @param {number} options.cacheTtl - Cache TTL in ms (default 60000)
   * @param {Function} options.onSuggestionsUpdated - Called when suggestions updated: (blockId, suggestions) => void
   * @param {Function} options.onBadgeClick - Called when badge clicked: (blockId, suggestions) => void
   * @param {Function} options.renderBadge - Custom badge rendering: (blockElement, suggestions, onClick) => void
   */
  constructor(options = {}) {
    // Get context from config manager if not provided
    const context = getPageContext()

    this.options = {
      websiteId: options.websiteId || context.websiteId,
      pageId: options.pageId || context.pageId,
      csrfToken: options.csrfToken || getCsrfToken(),
      cacheTtl: options.cacheTtl || DEFAULT_CACHE_TTL,
      onSuggestionsUpdated: null,
      onBadgeClick: null,
      renderBadge: null,
      ...options
    }

    // Suggestion cache: blockId -> { suggestions, timestamp }
    this.cache = {}

    // Track current block being viewed
    this.currentBlockId = null

    logger.log('Initialized for page:', this.options.pageId)
  }

  /**
   * Analyze a single block and return suggestions
   * @param {string} blockId - Block UUID
   * @returns {Promise<Array>} Suggestions array
   */
  async analyzeBlock(blockId) {
    // Check cache first
    const cached = this.cache[blockId]
    if (cached && Date.now() - cached.timestamp < this.options.cacheTtl) {
      logger.log('Using cached suggestions for block:', blockId)
      return cached.suggestions
    }

    try {
      const url = `/admin/websites/${this.options.websiteId}/pages/${this.options.pageId}/blocks/${blockId}/analyze`

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': this.options.csrfToken
        }
      })

      if (!response.ok) {
        logger.warn('Failed to analyze block:', blockId, response.status)
        return []
      }

      const data = await response.json()
      const suggestions = data.suggestions || []

      // Cache the result
      this.cache[blockId] = {
        suggestions,
        timestamp: Date.now()
      }

      logger.log('Analyzed block:', blockId, 'suggestions:', suggestions.length)

      // Notify listener
      if (this.options.onSuggestionsUpdated) {
        this.options.onSuggestionsUpdated(blockId, suggestions)
      }

      return suggestions
    } catch (error) {
      logger.error('Error analyzing block:', blockId, error)
      return []
    }
  }

  /**
   * Analyze all blocks on the page
   * @param {Document} doc - Document to search for blocks (default: document)
   * @returns {Promise<Map<string, Array>>} Map of blockId -> suggestions
   */
  async analyzeAllBlocks(doc = document) {
    const results = new Map()

    const blocks = doc.querySelectorAll('[data-block-id][data-lock-version]')
    logger.log('Found', blocks.length, 'blocks to analyze')

    for (const block of blocks) {
      const blockId = block.dataset.blockId
      if (!blockId) continue

      const suggestions = await this.analyzeBlock(blockId)
      results.set(blockId, suggestions)

      // Update badge on the block element
      this.updateBadge(block, suggestions)
    }

    logger.log('Analyzed all blocks, total:', results.size)
    return results
  }

  /**
   * Update suggestion badge on a block element
   * @param {HTMLElement} blockElement - Block element
   * @param {Array} suggestions - Suggestions array
   */
  updateBadge(blockElement, suggestions) {
    // Remove existing badge
    const existingBadge = blockElement.querySelector('.suggestion-badge')
    if (existingBadge) {
      existingBadge.remove()
    }

    if (!suggestions || suggestions.length === 0) {
      return
    }

    const blockId = blockElement.dataset.blockId

    // Use custom renderer if provided
    if (this.options.renderBadge) {
      this.options.renderBadge(
        blockElement,
        suggestions,
        () => this._handleBadgeClick(blockId, suggestions)
      )
      return
    }

    // Default badge rendering
    const severity = this.getHighestSeverity(suggestions)
    const badge = document.createElement('div')
    badge.className = `suggestion-badge severity-${severity}`
    badge.innerHTML = `
      ${SEVERITY_ICONS[severity] || SEVERITY_ICONS[Severity.INFO]}
      <span class="suggestion-badge-count">${suggestions.length}</span>
    `
    badge.title = `${suggestions.length} suggestion${suggestions.length > 1 ? 's' : ''} - Click to view`

    // Click handler
    badge.addEventListener('click', (e) => {
      e.stopPropagation()
      e.preventDefault()
      this._handleBadgeClick(blockId, suggestions)
    }, true)

    // Add to block
    blockElement.style.position = 'relative'
    blockElement.appendChild(badge)

    logger.log('Badge created for block:', blockId, 'count:', suggestions.length)
  }

  /**
   * Handle badge click
   * @private
   */
  _handleBadgeClick(blockId, suggestions) {
    this.currentBlockId = blockId

    if (this.options.onBadgeClick) {
      this.options.onBadgeClick(blockId, suggestions)
    }

    logger.log('Badge clicked for block:', blockId)
  }

  /**
   * Get highest severity from suggestions
   * @param {Array} suggestions
   * @returns {string} Severity level
   */
  getHighestSeverity(suggestions) {
    if (suggestions.some((s) => s.severity === Severity.ERROR)) return Severity.ERROR
    if (suggestions.some((s) => s.severity === Severity.WARNING)) return Severity.WARNING
    if (suggestions.some((s) => s.severity === Severity.INFO)) return Severity.INFO
    return Severity.INFO
  }

  /**
   * Get severity icon SVG
   * @param {string} severity
   * @returns {string} SVG string
   */
  getSeverityIcon(severity) {
    return SEVERITY_ICONS[severity] || SEVERITY_ICONS[Severity.INFO]
  }

  /**
   * Get cached suggestions for a block
   * @param {string} blockId
   * @returns {Array|null}
   */
  getCachedSuggestions(blockId) {
    const cached = this.cache[blockId]
    if (cached && Date.now() - cached.timestamp < this.options.cacheTtl) {
      return cached.suggestions
    }
    return null
  }

  /**
   * Invalidate cache for a block
   * @param {string} blockId - Block ID, or null to clear all
   */
  invalidateCache(blockId = null) {
    if (blockId) {
      delete this.cache[blockId]
      logger.log('Cache invalidated for block:', blockId)
    } else {
      this.cache = {}
      logger.log('Cache cleared')
    }
  }

  /**
   * Re-analyze a specific block (invalidates cache first)
   * @param {string} blockId
   * @param {HTMLElement} blockElement - Optional element to update badge
   * @returns {Promise<Array>}
   */
  async reanalyzeBlock(blockId, blockElement = null) {
    this.invalidateCache(blockId)
    const suggestions = await this.analyzeBlock(blockId)

    if (blockElement) {
      this.updateBadge(blockElement, suggestions)
    }

    return suggestions
  }

  /**
   * Set the current page ID (e.g., after navigation)
   * @param {string} pageId
   */
  setPageId(pageId) {
    if (pageId !== this.options.pageId) {
      this.options.pageId = pageId
      this.invalidateCache() // Clear cache for new page
      logger.log('Page ID updated to:', pageId)
    }
  }

  /**
   * Destroy the manager
   */
  destroy() {
    this.invalidateCache()
    this.currentBlockId = null
    logger.log('Destroyed')
  }
}
